import React, { useState } from 'react';
import { ChevronRight, Plus } from 'lucide-react';
import { NavActions } from '../components/NavBar/NavActions';
import { useAppMode } from '../contexts/AppModeContext';
// Mock wines available as gift bottles / gift choices (would come from Shopify)
const MOCK_WINES = ['Reserve Cabernet 2019', 'Estate Chardonnay 2021', 'Single-Vineyard Pinot 2020', 'Sparkling Brut NV', 'Late Harvest Riesling 2022'];
interface MilestoneConfig {
  bonusPoints: {
    enabled: boolean;
    points: string;
  };
  giftBottle: {
    enabled: boolean;
    product: string;
  };
  giftChoice: {
    enabled: boolean;
    products: string[];
  };
  allocation: {
    enabled: boolean;
    access: 'early' | 'exclusive';
  };
  tierProtection: {
    enabled: boolean;
    months: string;
  };
  email: {
    subject: string;
    preview: string;
    showInPortal: boolean;
  };
}
interface AnniversaryMilestone {
  id: string;
  label: string;
  chips: string[];
  email: string;
  config: MilestoneConfig;
}
const defaultConfig = (): MilestoneConfig => ({
  bonusPoints: {
    enabled: false,
    points: '500'
  },
  giftBottle: {
    enabled: false,
    product: MOCK_WINES[0]
  },
  giftChoice: {
    enabled: false,
    products: []
  },
  allocation: {
    enabled: false,
    access: 'early'
  },
  tierProtection: {
    enabled: false,
    months: '12'
  },
  email: {
    subject: '',
    preview: '',
    showInPortal: false
  }
});
const MILESTONES: AnniversaryMilestone[] = [{
  id: '1yr',
  label: '1 year',
  chips: ['500 bonus points'],
  email: "Sends: 'Thank you for your first year' email",
  config: {
    ...defaultConfig(),
    bonusPoints: {
      enabled: true,
      points: '500'
    },
    email: {
      subject: 'Thank you for your first year',
      preview: "It's been a year — here's a little something from us.",
      showInPortal: false
    }
  }
}, {
  id: '3yr',
  label: '3 years',
  chips: ['Reserve gift bottle', '1,000 bonus points'],
  email: "Sends: '3 years with us' email",
  config: {
    ...defaultConfig(),
    bonusPoints: {
      enabled: true,
      points: '1000'
    },
    giftBottle: {
      enabled: true,
      product: MOCK_WINES[0]
    },
    email: {
      subject: '3 years with us',
      preview: 'Three years in — enjoy a reserve bottle on us.',
      showInPortal: true
    }
  }
}, {
  id: '5yr',
  label: '5 years',
  chips: ['Allocation early access', 'Tier protected 12 months', '2,000 bonus points'],
  email: "Sends: '5 year milestone' email",
  config: {
    ...defaultConfig(),
    bonusPoints: {
      enabled: true,
      points: '2000'
    },
    allocation: {
      enabled: true,
      access: 'early'
    },
    tierProtection: {
      enabled: true,
      months: '12'
    },
    email: {
      subject: '5 year milestone',
      preview: 'Five years of membership — thank you for staying with us.',
      showInPortal: true
    }
  }
}];
// Small toggle switch reused throughout the panel
function Toggle({
  checked,
  onChange,
  label
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  return <button type="button" onClick={onChange} aria-pressed={checked} aria-label={label} className={`w-11 h-6 rounded-full transition-colors duration-200 ease-in-out relative flex-shrink-0 ${checked ? 'bg-[#0EA5E9]' : 'bg-[#CBD5E1]'}`} data-id="element-7234" data-mp="ythslbx7uhpf">
      <div className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ease-in-out ${checked ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-7235" data-mp="p2n9uhj6i08q" />
    </button>;
}
// A reward menu row: label + description on the left, toggle on the right,
// and its own config revealed below when enabled.
function RewardRow({
  title,
  description,
  enabled,
  onToggle,
  children
}: {
  title: string;
  description: string;
  enabled: boolean;
  onToggle: () => void;
  children?: React.ReactNode;
}) {
  return <div className="py-3" data-id="element-7236" data-mp="25flqx1urnz3p">
      <div className="flex items-start justify-between gap-4" data-id="element-7237" data-mp="20iv4lwsdc79u">
        <div className="pr-4" data-id="element-7238" data-mp="20gimnjmm1wws">
          <span className="text-sm font-medium text-[#0F172A]" data-id="element-7239" data-mp="x7xrds2zvvbg">{title}</span>
          <p className="text-xs text-[#64748B] mt-0.5" data-id="element-7240" data-mp="x5v2xchhqa8u">{description}</p>
        </div>
        <Toggle checked={enabled} onChange={onToggle} label={title} data-id="element-7241" data-mp="125zi8z6nwyxh" />
      </div>
      {enabled && children && <div className="mt-3 ml-0.5" data-id="element-7242" data-mp="o93wq7985f0j">{children}</div>}
    </div>;
}
const inputClass = 'h-9 px-3 border border-[#CBD5E1] rounded-md text-sm text-[#0F172A] focus:border-[#0EA5E9] focus:ring-1 focus:ring-[#0EA5E9] focus:outline-none';
export function AnniversaryRewardsPage() {
  // Allocations don't exist in loyalty-only mode, so the allocation reward is
  // hidden (both the menu option and any read-only chip that references it).
  const {
    isLoyaltyOnly
  } = useAppMode();
  const [anniversaryEnabled, setAnniversaryEnabled] = useState(true);
  const [milestones, setMilestones] = useState<AnniversaryMilestone[]>(MILESTONES);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const updateConfig = (id: string, updater: (config: MilestoneConfig) => MilestoneConfig) => {
    setMilestones(prev => prev.map(m => m.id === id ? {
      ...m,
      config: updater(m.config)
    } : m));
  };
  const toggleGiftChoiceProduct = (id: string, wine: string) => {
    updateConfig(id, c => {
      const has = c.giftChoice.products.includes(wine);
      return {
        ...c,
        giftChoice: {
          ...c.giftChoice,
          products: has ? c.giftChoice.products.filter(w => w !== wine) : [...c.giftChoice.products, wine]
        }
      };
    });
  };
  return <div className="bg-[#F1F5F9] min-h-full p-6" data-id="element-7243" data-mp="3r0ir8ql90995">
      {/* Header */}
      <div className="mb-6" data-id="element-7244" data-mp="9j9bn05jyi36">
        <div className="w-full h-16 bg-white border-b border-[#CBD5E1] px-6 flex items-center gap-4" data-id="element-7245" data-mp="9jizoi71ef68">
          <div data-id="element-7246" data-mp="4nasbqz2zq9m">
            <h1 className="text-lg font-semibold text-[#0F172A] m-0" data-id="element-7247" data-mp="4nkaisdvwt4r">
              Anniversary Rewards
            </h1>
            <p className="text-xs text-[#94A3B8] mt-0.5" data-id="element-7248" data-mp="ejxc0cfo8ba2">
              Thank members for every year they stay in the club.
            </p>
          </div>
          <div className="flex-1" data-id="element-7249" data-mp="loexeysgbv8h" />
          <NavActions data-id="element-7250" data-mp="qlf49bx4l20n" />
        </div>
      </div>

      <div className="max-w-4xl mx-auto space-y-6" data-id="element-7251" data-mp="qm6nrodg79ak">
        {/* Master toggle */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-6 shadow-sm" data-id="element-7252" data-mp="vjqcji7rv4gr">
          <div className="flex items-start justify-between gap-4" data-id="element-7253" data-mp="10bq86ygt7b7j">
            <div className="pr-4" data-id="element-7254" data-mp="14d3gdf7djqhm">
              <span className="text-base font-semibold text-[#0F172A]" data-id="element-7255" data-mp="14dv2lzf30bv8">
                Enable anniversary rewards
              </span>
              <p className="text-xs text-[#64748B] mt-1" data-id="element-7256" data-mp="19abpkqajok6q">
                Fires on each member's anniversary, counted from the date set in
                Program Settings, and sends their thank-you email.
              </p>
            </div>
            <Toggle checked={anniversaryEnabled} onChange={() => setAnniversaryEnabled(!anniversaryEnabled)} label="Enable anniversary rewards" data-id="element-7257" data-mp="3g2w1vdaqtp8x" />
          </div>
        </div>

        {/* Milestone ladder */}
        <div data-id="element-7258" data-mp="2274gcusquawa">
          <h2 className="text-sm font-semibold text-[#475569] uppercase tracking-wider mb-4" data-id="element-7259" data-mp="227mig8237jc4">
            Milestone ladder
          </h2>

          {/* Connected ascending timeline */}
          <div className="relative pl-8" data-id="element-7260" data-mp="1tdhlr809vdoo">
            {/* Vertical connector line down the left */}
            <div className="absolute left-3 top-3 bottom-3 w-px bg-[#E2E8F0]" data-id="element-7261" data-mp="1ofzavc2p2oe1" />

            <div className="space-y-4" data-id="element-7262" data-mp="2l56vmhvhotgw">
              {milestones.map((milestone, index) => {
              const isOpen = expandedId === milestone.id;
              const {
                config
              } = milestone;
              return <div key={milestone.id} className="relative" data-id="element-7263" data-mp="2c3pgp2ttixts" data-mpi={index}>
                    {/* Node on the connector — grows brighter up the ladder */}
                    <div className="absolute -left-[22px] top-6 w-3 h-3 rounded-full border-2 border-white ring-1 ring-[#CBD5E1]" style={{
                  backgroundColor: '#0EA5E9',
                  opacity: 0.45 + index * 0.275
                }} data-id="element-7264" data-mp="2781soqr2oapv" />

                    <div className="bg-white border border-[#E2E8F0] rounded-lg shadow-sm" data-id="element-7265" data-mp="2vryyk1o5gf01">
                      {/* Summary row */}
                      <div className="flex items-center gap-4 px-5 py-4" data-id="element-7266" data-mp="30ps5iwxa1d1d">
                        {/* Milestone label */}
                        <div className="w-24 flex-shrink-0" data-id="element-7267" data-mp="2qtf9hse9en3g">
                          <span className="text-lg font-semibold text-[#0F172A]" data-id="element-7268" data-mp="2qtxebdezm9mz">
                            {milestone.label}
                          </span>
                        </div>

                        {/* Reward chips + email line */}
                        <div className="min-w-0 flex-1" data-id="element-7269" data-mp="34tzvsyjdv11p">
                          <div className="flex flex-wrap items-center gap-2" data-id="element-7270" data-mp="34rdlir53y7fs">
                            {milestone.chips.filter(chip => !isLoyaltyOnly || !chip.toLowerCase().includes('allocation')).map(chip => <span key={chip} className="inline-flex items-center rounded-full bg-[#E0F2FE] px-2.5 py-1 text-xs font-medium text-[#0369A1]" data-id="element-7271" data-mp="1bex2j6afrld">
                                  {chip}
                                </span>)}
                          </div>
                          <p className="mt-2 text-xs text-[#94A3B8]" data-id="element-7272" data-mp="1ltqtc7gpgjhk">
                            {milestone.email}
                          </p>
                        </div>

                        {/* Controls */}
                        <div className="flex flex-shrink-0 items-center gap-2" data-id="element-7273" data-mp="1vlwmvlmlv8yv">
                          <button type="button" onClick={() => setExpandedId(isOpen ? null : milestone.id)} className="text-sm font-medium text-[#0EA5E9] hover:text-[#0284C7]" data-id="element-7274" data-mp="17w282yiq7abz">
                            Edit
                          </button>
                          <button type="button" onClick={() => setExpandedId(isOpen ? null : milestone.id)} aria-label={isOpen ? 'Collapse' : 'Expand'} data-id="element-7275" data-mp="1gtkxsg7becu7">
                            <ChevronRight className={`w-4 h-4 text-[#94A3B8] transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} data-id="element-7276" data-mp="2s9t32pe4s29k" />
                          </button>
                        </div>
                      </div>

                      {/* Inline edit panel */}
                      {isOpen && <div className="border-t border-[#E2E8F0] px-5 py-5 bg-[#F8FAFC]" data-id="element-7277" data-mp="2rpz4ytsott3j">
                          {/* Reward menu */}
                          <h3 className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-1" data-id="element-7278" data-mp="2mxo6lvdlifd3">
                            Rewards for this rung
                          </h3>
                          <div className="divide-y divide-[#E2E8F0]" data-id="element-7279" data-mp="2d2w6ajsmpeem">
                            {/* Bonus points */}
                            <RewardRow title="Bonus points" description="Award loyalty points on the anniversary." enabled={config.bonusPoints.enabled} onToggle={() => updateConfig(milestone.id, c => ({
                        ...c,
                        bonusPoints: {
                          ...c.bonusPoints,
                          enabled: !c.bonusPoints.enabled
                        }
                      }))} data-id="element-7280" data-mp="284cgu9ettkhi">
                              <div data-id="element-7281" data-mp="4tzjrd82qs99">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7282" data-mp="4swrp1z0mjrr">
                                  Points
                                </label>
                                <input type="number" value={config.bonusPoints.points} onChange={e => updateConfig(milestone.id, c => ({
                            ...c,
                            bonusPoints: {
                              ...c.bonusPoints,
                              points: e.target.value
                            }
                          }))} className={`${inputClass} w-32`} data-id="element-7283" data-mp="ej9but2x4axd" />
                              </div>
                            </RewardRow>

                            {/* Free gift bottle */}
                            <RewardRow title="Free gift bottle" description="Add a single bottle to their next box." enabled={config.giftBottle.enabled} onToggle={() => updateConfig(milestone.id, c => ({
                        ...c,
                        giftBottle: {
                          ...c.giftBottle,
                          enabled: !c.giftBottle.enabled
                        }
                      }))} data-id="element-7284" data-mp="3w1bw9ey0ihiu">
                              <div data-id="element-7285" data-mp="3hb41rd853ltv">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7286" data-mp="3ha1fxjpoc9kt">
                                  Product
                                </label>
                                <select value={config.giftBottle.product} onChange={e => updateConfig(milestone.id, c => ({
                            ...c,
                            giftBottle: {
                              ...c.giftBottle,
                              product: e.target.value
                            }
                          }))} className={`${inputClass} w-full max-w-xs bg-white`} data-id="element-7287" data-mp="2olx74pbt3gdw">
                                  {MOCK_WINES.map(wine => <option key={wine} value={wine} data-id="element-7288" data-mp="25ucnictniicm">
                                      {wine}
                                    </option>)}
                                </select>
                                <p className="text-xs text-[#94A3B8] mt-1.5" data-id="element-7289" data-mp="gd8qursvd1ar">
                                  Added as a one-time gift, falls off after
                                  their next box.
                                </p>
                              </div>
                            </RewardRow>

                            {/* Gift choice */}
                            <RewardRow title="Gift choice" description="Offer a few bottles for the member to choose one from." enabled={config.giftChoice.enabled} onToggle={() => updateConfig(milestone.id, c => ({
                        ...c,
                        giftChoice: {
                          ...c.giftChoice,
                          enabled: !c.giftChoice.enabled
                        }
                      }))} data-id="element-7290" data-mp="102j7q94cpsag">
                              <div data-id="element-7291" data-mp="3atj1yllrxfj7">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7292" data-mp="3ftnl6je2god4">
                                  Choices offered
                                </label>
                                <div className="flex flex-wrap gap-2" data-id="element-7293" data-mp="3qcviom4ugv9d">
                                  {MOCK_WINES.map(wine => {
                              const selected = config.giftChoice.products.includes(wine);
                              return <button key={wine} type="button" onClick={() => toggleGiftChoiceProduct(milestone.id, wine)} className={`px-3 h-8 rounded-full text-xs font-medium border transition-colors ${selected ? 'bg-[#E0F2FE] border-[#0EA5E9] text-[#0369A1]' : 'bg-white border-[#CBD5E1] text-[#475569] hover:border-[#94A3B8]'}`} data-id="element-7294" data-mp="3hcp6r4oso3cb">
                                        {wine}
                                      </button>;
                            })}
                                </div>
                              </div>
                            </RewardRow>

                            {/* Allocation access — hidden in loyalty-only mode
                             since allocations don't exist there. */}
                            {!isLoyaltyOnly && <RewardRow title="Allocation access" description="Give priority access to limited allocations." enabled={config.allocation.enabled} onToggle={() => updateConfig(milestone.id, c => ({
                        ...c,
                        allocation: {
                          ...c.allocation,
                          enabled: !c.allocation.enabled
                        }
                      }))} data-id="element-7295" data-mp="2eleabphmc70b">
                                <div className="flex items-center gap-6" data-id="element-7296" data-mp="328vw94e7cavd">
                                  {([['early', 'Early access'], ['exclusive', 'Exclusive access']] as const).map(([value, text]) => <label key={value} className="flex items-center gap-2 cursor-pointer" data-id="element-7297" data-mp="3765sp400dmsp">
                                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${config.allocation.access === value ? 'border-[#0EA5E9]' : 'border-[#CBD5E1]'}`} onClick={() => updateConfig(milestone.id, c => ({
                              ...c,
                              allocation: {
                                ...c.allocation,
                                access: value
                              }
                            }))} data-id="element-7298" data-mp="3c4z7czr17jug">
                                        {config.allocation.access === value && <div className="w-2 h-2 rounded-full bg-[#0EA5E9]" data-id="element-7299" data-mp="1er67ol7wt51k" />}
                                      </div>
                                      <span className="text-sm text-[#0F172A]" data-id="element-7300" data-mp="1spw5k32jz8qz">
                                        {text}
                                      </span>
                                    </label>)}
                                </div>
                              </RewardRow>}

                            {/* Tier protection */}
                            <RewardRow title="Tier protection" description="Protect the member's current tier for a set time." enabled={config.tierProtection.enabled} onToggle={() => updateConfig(milestone.id, c => ({
                        ...c,
                        tierProtection: {
                          ...c.tierProtection,
                          enabled: !c.tierProtection.enabled
                        }
                      }))} data-id="element-7301" data-mp="2bhyhx285w2vf">
                              <div data-id="element-7302" data-mp="2h3h413v29lcs">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7303" data-mp="2m3245hlyvzr3">
                                  Months protected
                                </label>
                                <input type="number" value={config.tierProtection.months} onChange={e => updateConfig(milestone.id, c => ({
                            ...c,
                            tierProtection: {
                              ...c.tierProtection,
                              months: e.target.value
                            }
                          }))} className={`${inputClass} w-32`} data-id="element-7304" data-mp="27ccr09oy4akl" />
                              </div>
                            </RewardRow>
                          </div>

                          {/* Thank-you email block */}
                          <div className="mt-5 pt-5 border-t border-[#E2E8F0]" data-id="element-7305" data-mp="28vmq8p3meyw1">
                            <h3 className="text-xs font-semibold text-[#475569] uppercase tracking-wider mb-3" data-id="element-7306" data-mp="23ymjsd2uzu4e">
                              Thank-you email
                            </h3>
                            <div className="space-y-3" data-id="element-7307" data-mp="1uvlhyol10p6j">
                              <div data-id="element-7308" data-mp="1pxjwjx04odrw">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7309" data-mp="1pyliiwiomkoe">
                                  Subject line
                                </label>
                                <input type="text" value={config.email.subject} onChange={e => updateConfig(milestone.id, c => ({
                            ...c,
                            email: {
                              ...c.email,
                              subject: e.target.value
                            }
                          }))} className={`${inputClass} w-full bg-white`} data-id="element-7310" data-mp="1g6z1uknkrte9" />
                              </div>
                              <div data-id="element-7311" data-mp="3dv2v2mkpa58u">
                                <label className="block text-xs font-medium text-[#475569] mb-1.5" data-id="element-7312" data-mp="3iu5p7bz56r21">
                                  Preview line
                                </label>
                                <input type="text" value={config.email.preview} onChange={e => updateConfig(milestone.id, c => ({
                            ...c,
                            email: {
                              ...c.email,
                              preview: e.target.value
                            }
                          }))} className={`${inputClass} w-full bg-white`} data-id="element-7313" data-mp="3nrnwyz1fb08g" />
                              </div>
                              <div className="flex items-center justify-between pt-1" data-id="element-7314" data-mp="38whwssp0dvn1">
                                <span className="text-sm text-[#0F172A]" data-id="element-7315" data-mp="2cev8bx3xirwm">
                                  Also show in member portal
                                </span>
                                <Toggle checked={config.email.showInPortal} onChange={() => updateConfig(milestone.id, c => ({
                            ...c,
                            email: {
                              ...c.email,
                              showInPortal: !c.email.showInPortal
                            }
                          }))} label="Also show in member portal" data-id="element-7316" data-mp="1nuhe5lz49je5" />
                              </div>
                            </div>
                          </div>

                          {/* Footer */}
                          <div className="mt-5 pt-4 border-t border-[#E2E8F0] flex justify-end gap-2" data-id="element-7317" data-mp="196mfzq2camto">
                            <button type="button" onClick={() => setExpandedId(null)} className="h-9 px-4 border border-[#CBD5E1] rounded-md text-sm font-medium text-[#475569] bg-white hover:bg-[#F1F5F9] transition-colors" data-id="element-7318" data-mp="1e3w6l91gt4j3">
                              Cancel
                            </button>
                            <button type="button" onClick={() => setExpandedId(null)} className="h-9 px-4 rounded-md text-sm font-medium text-white bg-[#0EA5E9] hover:bg-[#0284C7] transition-colors" data-id="element-7319" data-mp="qdsdvwptep6v">
                              Done
                            </button>
                          </div>
                        </div>}
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Add milestone */}
          <button type="button" className="mt-4 ml-8 inline-flex items-center gap-2 rounded-md border border-dashed border-[#CBD5E1] bg-white px-4 h-10 text-sm font-medium text-[#475569] hover:border-[#0EA5E9] hover:text-[#0EA5E9] transition-colors" data-id="element-7320" data-mp="3w1uaoa87qg4j">
            <Plus className="w-4 h-4" data-id="element-7321" data-mp="3mcu86gui52ll" />
            Add anniversary milestone
          </button>
        </div>
      </div>
    </div>;
}